// src/hooks/usePuMapLayer.js
// ─────────────────────────────────────────────────────────────────────────────
// FlowSense MOW+ — Public Utilities map data
// --- UPDATE (Public Utilities v3 — PU-only map) — NEW FILE ---------------------------------------
// Loads GET /public-utilities/map (only Public Utilities assets with real GPS, constituency
// polygons, managers and legend counts) and keeps it live:
//   • `version` — pass usePublicUtilitiesLive().puVersion; every accepted Public Utilities event
//     (inspection, maintenance, status, import, manager / zone change, asset CRUD anywhere) reloads
//     the layer, coalesced to one request per burst (default 1.5 s)
//   • while a reload runs the previous layer stays on screen (no flicker, no empty map)
//   • the server answers with ETag / 304 and gzip, so an unchanged reload costs a few hundred bytes
//   • a failed load keeps the last good layer and reports `error`; `available:false` means the
//     backend route is not deployed — the page falls back to the classic asset markers
// --- END UPDATE ------------------------------------------------------------------------------
import { useCallback, useEffect, useRef, useState } from 'react';
import { getMapLayer } from '../services/publicUtilitiesApi';

export const PU_MAP_COALESCE_MS = 1500;

export default function usePuMapLayer({ enabled = true, version = 0, filters = null, coalesceMs = PU_MAP_COALESCE_MS } = {}) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [available, setAvailable] = useState(true);
    const [loadedAt, setLoadedAt] = useState(null);
    const aliveRef = useRef(true);
    const seqRef = useRef(0);
    const timerRef = useRef(null);
    const filtersKey = filters ? JSON.stringify(filters) : '';

    const load = useCallback(async () => {
        const seq = ++seqRef.current;
        setLoading(true);
        try {
            const layer = await getMapLayer(filters || {});
            if (!aliveRef.current || seq !== seqRef.current) return;
            setData(layer); setError(null); setAvailable(true); setLoadedAt(Date.now());
        } catch (e) {
            if (!aliveRef.current || seq !== seqRef.current) return;
            const status = e?.status ?? e?.response?.status;
            if (status === 404) setAvailable(false);
            setError(e?.message || 'Could not load the Public Utilities map.');
        } finally {
            if (aliveRef.current && seq === seqRef.current) setLoading(false);
        }
    }, [filtersKey]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { aliveRef.current = true; return () => { aliveRef.current = false; clearTimeout(timerRef.current); }; }, []);

    // first load + filter changes: immediately
    useEffect(() => { if (enabled) load(); }, [enabled, load]);

    // live events: coalesced
    const firstVersion = useRef(version);
    const pendingRef = useRef(false);
    useEffect(() => {
        if (!enabled || version === firstVersion.current) return undefined;
        clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => {
            if (typeof document !== 'undefined' && document.visibilityState === 'hidden') { pendingRef.current = true; return; }
            load();
        }, coalesceMs);
        return () => clearTimeout(timerRef.current);
    }, [version, enabled, load, coalesceMs]);
    // caught up when the browser tab becomes visible again
    useEffect(() => {
        if (typeof document === 'undefined') return undefined;
        const onVis = () => { if (document.visibilityState === 'visible' && pendingRef.current && enabled) { pendingRef.current = false; load(); } };
        document.addEventListener('visibilitychange', onVis);
        return () => document.removeEventListener('visibilitychange', onVis);
    }, [enabled, load]);

    return { data, loading, error, available, loadedAt, reload: load };
}
